import { type SearchResultFragment, useLinkConversationToInsuredMutation } from "@qw/graphql";
import {
  ButtonToggle,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from "@qw/ui";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useMatch } from "react-router";
import { useInboxRailLayout } from "@/inbox/use-inbox-rail";
import { Active } from "@/mini-app/active/active";
import { LinkInsuredSearch } from "@/mini-app/components/link-insured-search";
import { MiniAppActions } from "@/mini-app/mini-app-actions";
import { MiniAppHeader } from "@/mini-app/mini-app-header";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { useConversation } from "@/providers/use-conversation";
import { useInboxInsured } from "@/providers/use-inbox-insured";
import { usePage } from "@/providers/use-page";

export const MiniAppToggle = ({ className }: { className?: string }) => {
  const inboxMatch = useMatch("/inbox/*");
  const { pathname } = useLocation();
  const { isMergedRail } = useInboxRailLayout();
  const { isMiniAppOpen, rootPath, setIsMiniAppOpen } = usePage();
  const { conversationId } = useInboxQueryStates();
  const { inboxConversation, loading: conversationLoading } = useConversation();
  const { insured, insuredId, loading: insuredLoading } = useInboxInsured();
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const [linkInsured] = useLinkConversationToInsuredMutation({
    refetchQueries: ["InboxConversation"],
  });
  const selectedConversation = conversationId
    ? inboxConversation?.id === conversationId
      ? inboxConversation
      : undefined
    : undefined;
  const hasLinkedInsured = Boolean(selectedConversation?.insuredId);
  const routeInsuredId = pathname.startsWith(`${rootPath}/`)
    ? pathname.slice(rootPath.length + 1).split("/")[0]
    : undefined;
  const loadingRouteInsuredId = conversationLoading && routeInsuredId !== "files" ? routeInsuredId : undefined;
  const toggleInsuredId = insuredId ?? loadingRouteInsuredId;
  const selectedConversationPending = Boolean(conversationId && conversationLoading && !selectedConversation);
  const canToggle = Boolean(selectedConversation || toggleInsuredId || selectedConversationPending);
  const icon = selectedConversation && !hasLinkedInsured ? "database_off" : "database";
  const isInboxRoute = Boolean(inboxMatch);
  const loading = Boolean(conversationLoading || insuredLoading);
  const rendersHoverCard = Boolean(isInboxRoute && !isMergedRail && canToggle && !isMiniAppOpen);

  useEffect(() => {
    if (!rendersHoverCard) {
      setOpen(false);
    }
  }, [rendersHoverCard]);

  const handleLinkInsured = useCallback(
    async ({ insuredId }: SearchResultFragment) => {
      if (!selectedConversation) {
        return;
      }

      const { data } = await linkInsured({
        variables: { input: { conversationId: selectedConversation.id, insuredId } },
      });
      const redirect = data?.linkConversationToInsured?.insuredLinkRedirect;
      if (redirect) {
        toast({ description: redirect.message });
      }
      setOpen(false);
    },
    [linkInsured, selectedConversation, toast]
  );
  const handleToggle = useCallback(() => {
    setIsMiniAppOpen((current) => !current);
  }, [setIsMiniAppOpen]);

  const toggle = (
    <ButtonToggle
      active={Boolean(canToggle && isMiniAppOpen)}
      aria-label="Toggle insured panel"
      className={className}
      disabled={!canToggle}
      onClick={handleToggle}
    >
      <Icon icon={icon} />
    </ButtonToggle>
  );

  if (!isInboxRoute || isMergedRail) {
    return null;
  }

  if (!canToggle || isMiniAppOpen) {
    // No hover card on this path, so the toggle can carry a tooltip without the two competing.
    return (
      <Tooltip>
        <TooltipTrigger asChild>{toggle}</TooltipTrigger>
        <TooltipContent>{isMiniAppOpen ? "Hide insured panel" : "Show insured panel"}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={300}>
      <HoverCardTrigger asChild>{toggle}</HoverCardTrigger>
      <HoverCardContent align="end" side="bottom" sideOffset={8} className="w-96 max-w-[calc(100dvw-1rem)] p-2">
        {selectedConversation && !hasLinkedInsured ? (
          <LinkInsuredSearch autoFocus={false} onSelect={handleLinkInsured} />
        ) : !loading && !insured ? (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 p-2 text-muted-foreground">
              <Icon icon={selectedConversation ? "link_off" : "database_off"} />
              <h4 className="flex-auto truncate font-medium text-base text-foreground">No Insured</h4>
            </div>
            {selectedConversation && <LinkInsuredSearch autoFocus={false} onSelect={handleLinkInsured} />}
          </div>
        ) : (
          <div className="flex max-h-[calc(100dvh-8rem)] flex-col overflow-hidden">
            <div className="flex flex-none items-center gap-2 pb-2">
              <MiniAppHeader insured={insured} loading={loading} />
              <MiniAppActions
                inboxConversation={selectedConversation}
                insured={insured}
                insuredId={insuredId}
                rootPath={rootPath}
                // This popover is already a hover card. A nested one is portalled out
                // of its content, so moving the pointer into it closes both.
                showInsuredHoverCard={false}
              />
            </div>
            {insured && (
              <div className="-mx-2 min-h-0 flex-auto overflow-y-auto">
                <Active liveUpdates={false} showOpportunitySubmissions={false} />
              </div>
            )}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};
